import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Stack, TextField, Typography, Alert, MenuItem, Select, FormControl, InputLabel, Chip } from '@mui/material';
import { apiService } from '../../services/api';

const ROLE_LABELS = {
  admin: 'Admin',
  mitarbeiter: 'Mitarbeiter',
  zeiterfasser: 'Zeiterfasser',
  viewer: 'Viewer'
};

export default function EinladungenPage() {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [members, setMembers] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [audit, setAudit] = useState([]);

  const loadMembers = async () => {
    try {
      const data = await apiService.get('/api/mandanten/mitglieder');
      setMembers(data?.mitglieder || []);
    } catch (error) {
      console.error(error);
      setError(error?.message || 'Mitglieder konnten nicht geladen werden.');
    }
  };

  const loadAudit = async () => {
    try {
      const data = await apiService.get('/api/mandanten/audit');
      setAudit(data?.audit || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    loadMembers();
    loadAudit();
  }, []);

  const handleInvite = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');
    try {
      await apiService.post('/api/mandanten/mitglieder/einladen', { email, role });
      setMessage('Einladung wurde vorbereitet.');
      setEmail('');
      await loadMembers();
      await loadAudit();
    } catch (error) {
      setError(error?.message || 'Einladung konnte nicht vorbereitet werden.');
    } finally {
      setLoading(false);
    }
  };

  const activeMembers = members.filter((member) => !member.removed_at);
  const adminCount = activeMembers.filter((member) => member.role === 'admin').length;
  const zeiterfasserCount = activeMembers.filter((member) => member.role === 'zeiterfasser').length;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>Team & Rechte</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Verwalten Sie Zugänge, Rollen und Nachvollziehbarkeit im Mandantenkontext.
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">Aktive Mitglieder</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{activeMembers.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">Admins</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{adminCount}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">Zeiterfasser</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{zeiterfasserCount}</Typography>
          </CardContent>
        </Card>
      </Stack>

      {message ? <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert> : null}
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Rollenleitfaden</Typography>
          <Stack spacing={1}>
            <Typography variant="body2"><strong>Admin:</strong> Kann Team, Rechte und Kernkonfiguration verwalten.</Typography>
            <Typography variant="body2"><strong>Mitarbeiter:</strong> Zugriff auf operative Bereiche gemäß Modulrechten.</Typography>
            <Typography variant="body2"><strong>Zeiterfasser:</strong> Fokus auf Mitarbeiterportal und Zeitbuchungen.</Typography>
            <Typography variant="body2"><strong>Viewer:</strong> Lesender Zugriff ohne mutierende Änderungen.</Typography>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Mitglied einladen</Typography>
          <form onSubmit={handleInvite}>
            <Stack spacing={2}>
              <TextField label="E-Mail" value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth />
              <FormControl fullWidth>
                <InputLabel>Rolle</InputLabel>
                <Select label="Rolle" value={role} onChange={(e) => setRole(e.target.value)}>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="mitarbeiter">Mitarbeiter</MenuItem>
                  <MenuItem value="zeiterfasser">Zeiterfasser</MenuItem>
                  <MenuItem value="viewer">Viewer</MenuItem>
                </Select>
              </FormControl>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? 'Wird vorbereitet…' : 'Einladung vorbereiten'}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Aktive Mitglieder</Typography>
          {members.length === 0 ? (
            <Typography color="text.secondary">Noch keine Mitglieder angelegt.</Typography>
          ) : (
            <Stack spacing={1}>
              {members.map((member) => (
                <Box key={member.membership_id || member.user_id} sx={{ border: '1px solid #e2e8f0', borderRadius: 1, p: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography fontWeight={600}>User {member.user_id}</Typography>
                    <Chip label={ROLE_LABELS[member.role] || member.role || 'Viewer'} size="small" color={member.role === 'admin' ? 'primary' : member.role === 'mitarbeiter' ? 'secondary' : member.role === 'zeiterfasser' ? 'success' : 'default'} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Status: {member.removed_at ? 'Entfernt' : 'Aktiv'}</Typography>
                  <Typography variant="body2" color="text.secondary">Eingeladen am: {member.invited_at ? new Date(member.invited_at).toLocaleDateString('de-DE') : '—'}</Typography>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Audit-Log</Typography>
          {audit.length === 0 ? (
            <Typography color="text.secondary">Noch keine Audit-Einträge vorhanden.</Typography>
          ) : (
            <Stack spacing={1}>
              {audit.slice(0, 8).map((entry, index) => (
                <Box key={`${entry.entity}-${entry.entity_id || index}`} sx={{ border: '1px solid #e2e8f0', borderRadius: 1, p: 2 }}>
                  <Typography fontWeight={600}>{entry.action}</Typography>
                  <Typography variant="body2" color="text.secondary">{entry.entity || '—'} • {entry.entity_id || '—'}</Typography>
                  <Typography variant="body2" color="text.secondary">{entry.created_at ? new Date(entry.created_at).toLocaleString('de-DE') : '—'}</Typography>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
